import React, { useState } from 'react';

import { ListItemIcon, MenuItem } from '@mui/material';
import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { useProgramLibraryTableStore } from 'store/programLibraryTable.store';
import { useVersionStore } from 'store/version.store';

import { useMutationRemoveXerFile } from 'api/mutations/removeXerFile.mutation';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import Icon from 'components/common/Icons/base/Icon/Icon';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import { initialFiltersState } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { Checkbox } from 'components/DesignSystem/Checkbox';
import { CustomizedConfirmDeleteModal, CustomizedRoundIcon, CustomizedTooltip } from 'components/common';
import ActionMenu from 'components/widgets/RenderLibraryTable/components/ActionMenu/ActionMenu';
import { ActionButtonMenu } from 'components/widgets/RenderLibraryTable/components/ActionMenu/components/ActionButtonMenu/ActionButtonMenu';
import classes from 'components/widgets/RenderLibraryTable/components/RowOptionsHeader/rowOptionsHeader.module.scss';

export const RowOptionsHeader = ({ toggleAllRowsSelected, isAllRowsSelected, selectedFlatRows }) => {
    const isMultiple = useProgramLibraryTableStore((state) => state.isMultiple);
    const setIsMultiple = useProgramLibraryTableStore((state) => state.setIsMultiple);
    const [showModal, setShowModal] = useState<boolean>(false);
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();
    const { mutate, isLoading } = useMutationRemoveXerFile();

    const { sendEventWithDimensions } = useEventWithDimensions();

    const { setVersion } = useVersionStore();
    const updateReplace = useGlobalFilterDataStore((state) => state.updateReplace);

    const handleDeleteFiles = () => {
        const ids = selectedFlatRows.map((item) => item.original.id);
        mutate(
            { ids },
            {
                onSuccess: () => {
                    handleSnackBar('File was deleted', 'success');
                    setVersion(null);
                    updateReplace(initialFiltersState);

                    sendEventWithDimensions({
                        category: 'Program Library',
                        action: 'Delete Version',
                        label: `version ids: ${ids}`,
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    setShowModal(false);
                    return queryClient.invalidateQueries('getProjectVersions');
                },
            },
        );
    };

    return (
        <>
            <div className={classes.container}>
                {isMultiple && <Checkbox checked={isAllRowsSelected} onChange={toggleAllRowsSelected} />}
                <CustomizedTooltip
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={'Multiple Selection'}
                    triggerElement={
                        <CustomizedRoundIcon
                            data-testid={'multiple-selection'}
                            size={'30px'}
                            onClick={() => setIsMultiple()}
                            selected={isMultiple}
                        >
                            <Icon name={'multiple-selection'} color={'var(--button-tertiary-fg)'} />
                        </CustomizedRoundIcon>
                    }
                />
                {isMultiple && (
                    <>
                        <ActionMenu
                            versionIdList={selectedFlatRows.map((item) => item.original.versionHashCode)}
                            status={'ok'}
                            errorMessage={''}
                            isMultiple={true}
                            id={`viewAnalytics_multiple`}
                        />
                        <ActionButtonMenu id={`threeDots_multiple`} tooltipContent={'Delete Files'}>
                            <MenuItem onClick={() => setShowModal(true)} disabled={selectedFlatRows.length === 0}>
                                <ListItemIcon>
                                    <MuiIcon icon={'delete'} type={'outlined'} />
                                </ListItemIcon>
                                Delete
                            </MenuItem>
                        </ActionButtonMenu>
                    </>
                )}
            </div>
            <CustomizedConfirmDeleteModal
                isShowModal={showModal}
                hideModal={() => {
                    setShowModal(false);
                }}
                text={
                    'Are you sure you want to delete this program version? Note all its associated cards will be deleted.'
                }
                onConfirm={handleDeleteFiles}
                isLoading={isLoading}
            />
        </>
    );
};
